import ScaleIcon from '@mui/icons-material/Scale';
import {
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@mui/material';
import { useEffect, useRef, type FocusEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isScaleShortcut } from '../../domain/scaleReader';

interface MetalFieldsProps {
    enabled: boolean;
    cantitate: number;
    onFieldChange: (field: string, value: unknown) => void;
    handleQuantityChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * Reads the scale into this quantity. Given only when the workstation's hardware service offers a
     * scale; the icon button and F2 exist only then.
     */
    onReadScale?: () => void;
    /** A scale read for this row is running. */
    scaleReading?: boolean;
}

export const MetalFields = ({
    enabled,
    cantitate,
    onFieldChange,
    handleQuantityChange,
    onReadScale,
    scaleReading = false,
}: MetalFieldsProps) => {
    const { t } = useTranslation();
    const quantityRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (enabled) {
            quantityRef.current?.focus();
        }
    }, [enabled]);

    const defaultQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numValue = value === '' ? 0 : Number(value);
        if (!isNaN(numValue) && numValue >= 0) {
            onFieldChange('cantitate', numValue);
        }
    };

    const canReadScale = onReadScale !== undefined && enabled && !scaleReading;

    // F2, the successor of the Delphi F3: the weight goes into the field that has the focus.
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (onReadScale !== undefined && isScaleShortcut(event)) {
            event.preventDefault();

            if (canReadScale) {
                onReadScale();
            }
        }
    };

    const quantityField = (
        <TextField
            size="small"
            label={t('contracts.quantity')}
            type="number"
            value={cantitate === 0 ? '' : cantitate}
            onChange={handleQuantityChange ?? defaultQuantityChange}
            disabled={!enabled}
            sx={{ width: 80 }}
            slotProps={{
                htmlInput: {
                    ref: quantityRef,
                    onFocus: (e: FocusEvent<HTMLInputElement>) => e.target.select(),
                    onKeyDown: handleKeyDown,
                },
            }}
        />
    );

    if (onReadScale === undefined) {
        return quantityField;
    }

    return (
        <>
            {quantityField}
            <Tooltip title={t('scale.readTooltip')}>
                <span>
                    <IconButton
                        size="small"
                        aria-label={t('scale.readTooltip')}
                        onClick={onReadScale}
                        disabled={!canReadScale}
                        data-testid="scale-read-button"
                    >
                        {scaleReading ? (
                            <CircularProgress size={18} />
                        ) : (
                            <ScaleIcon fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );
};

interface StonePercentFieldProps {
    enabled: boolean;
    piatraPercent: number;
    stonePercentOptions?: number[];
    onFieldChange: (field: string, value: unknown) => void;
}

export const StonePercentField = ({
    enabled,
    piatraPercent,
    stonePercentOptions,
    onFieldChange,
}: StonePercentFieldProps) => {
    const { t } = useTranslation();

    return stonePercentOptions ? (
        <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>{t('contracts.stonePercent')}</InputLabel>
            <Select
                value={piatraPercent ?? ''}
                onChange={e => onFieldChange('piatraPercent', Number(e.target.value))}
                label={t('contracts.stonePercent')}
                disabled={!enabled}
            >
                {stonePercentOptions.map(percent => (
                    <MenuItem key={percent} value={percent}>
                        {percent}%
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    ) : (
        <TextField
            size="small"
            label={t('contracts.stonePercent')}
            type="number"
            value={piatraPercent === 0 ? '' : piatraPercent}
            onChange={e => {
                const value = e.target.value;
                const numValue = value === '' ? 0 : Number(value);
                if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                    onFieldChange('piatraPercent', numValue);
                }
            }}
            disabled={!enabled}
            sx={{ width: 80 }}
        />
    );
};

interface StoneWeightFieldProps {
    enabled: boolean;
    piatra: number;
}

export const StoneWeightField = ({ enabled, piatra }: StoneWeightFieldProps) => {
    const { t } = useTranslation();

    return (
        <TextField
            size="small"
            label={t('contracts.stoneWeight')}
            type="number"
            value={piatra ? piatra.toFixed(2) : ''}
            slotProps={{ input: { readOnly: true } }}
            disabled={!enabled}
            sx={{ width: 80 }}
        />
    );
};

interface ProposedFieldProps {
    enabled: boolean;
    propus: number;
}

export const ProposedField = ({ enabled, propus }: ProposedFieldProps) => {
    const { t } = useTranslation();

    return (
        <TextField
            size="small"
            label={t('contracts.proposed')}
            type="number"
            value={propus ? propus.toFixed(2) : ''}
            slotProps={{ input: { readOnly: true } }}
            disabled={!enabled}
            sx={{ width: 80 }}
        />
    );
};

interface StoneFieldsProps {
    enabled: boolean;
    piatraPercent: number;
    piatra: number;
    propus: number;
    stonePercentOptions?: number[];
    onFieldChange: (field: string, value: unknown) => void;
}

export const StoneFields = ({
    enabled,
    piatraPercent,
    piatra,
    propus,
    stonePercentOptions,
    onFieldChange,
}: StoneFieldsProps) => {
    return (
        <>
            <StonePercentField
                enabled={enabled}
                piatraPercent={piatraPercent}
                stonePercentOptions={stonePercentOptions}
                onFieldChange={onFieldChange}
            />
            <StoneWeightField enabled={enabled} piatra={piatra} />
            <ProposedField enabled={enabled} propus={propus} />
        </>
    );
};

interface PxCheckboxProps {
    enabled: boolean;
    px: boolean;
    onFieldChange: (field: string, value: unknown) => void;
}

export const PxCheckbox = ({ enabled, px, onFieldChange }: PxCheckboxProps) => {
    return (
        <FormControlLabel
            control={
                <Checkbox
                    checked={px}
                    onChange={e => onFieldChange('px', e.target.checked)}
                    disabled={!enabled}
                />
            }
            label="PX"
        />
    );
};
